import { Link } from "react-router-dom";
import { Sparkles, Coffee, Hand, Moon, Star, Heart, Wheat, Layers, HeartHandshake, ArrowRight, ShieldCheck } from "lucide-react";
import { HERO_IMAGES, FORTUNE_COVERS } from "@/lib/images";

const features = [
  { icon: Coffee, key: "kahve", name: "Kahve Falı", desc: "Fincanını çek, kaderi oku" },
  { icon: Hand, key: "el", name: "El Falı", desc: "Avucundaki kadere bak" },
  { icon: Sparkles, key: "tarot", name: "Tarot Falı", desc: "22 major + minor kart" },
  { icon: Moon, key: "ruya", name: "Rüya Yorumu", desc: "İslami · Freud · Jung" },
  { icon: Wheat, key: "pirinc", name: "Pirinç Falı", desc: "Sorunun cevabı burada" },
  { icon: Layers, key: "iskambil", name: "İskambil Falı", desc: "5 kartlık açılım" },
  { icon: Heart, key: "katina", name: "Katina", desc: "Aşk odaklı yorum" },
  { icon: Star, key: "burc", name: "Günlük Burç", desc: "Her sabah taze yorum" },
  { icon: HeartHandshake, key: "ask", name: "Aşk Uyumu", desc: "İsim + burç uyum skoru" },
];

export default function Landing() {
  return (
    <div className="cosmic-bg min-h-screen">
      <div className="stars" />
      <header className="sticky top-0 z-40 glass-strong">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 gold-text" />
            <span className="font-serif-myst text-2xl font-semibold">FalZilla</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/giris" data-testid="landing-login-link" className="btn-ghost px-4 py-2 rounded-full text-sm">Giriş</Link>
            <Link to="/kayit" data-testid="landing-register-link" className="btn-gold px-4 py-2 rounded-full text-sm">Ücretsiz Başla</Link>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-16 md:py-28 grid md:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-xs uppercase tracking-widest text-white/70 mb-6">
            <ShieldCheck className="w-3.5 h-3.5 gold-text" />
            <span>Yeni gelenlere 15 jeton hediye</span>
          </div>
          <h1 className="font-serif-myst text-5xl md:text-7xl leading-[0.95] font-semibold mb-6">
            Yıldızlar sana ne <span className="gold-text italic">fısıldıyor?</span>
          </h1>
          <p className="text-white/70 text-lg md:text-xl leading-relaxed mb-10 max-w-lg">
            Kahve, el, tarot, rüya, katina ve daha fazlası. Yapay zekâ destekli yorumlar; ustaların sıcaklığında.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/kayit" data-testid="hero-cta-register" className="btn-gold px-8 py-3.5 rounded-full text-base flex items-center gap-2 animate-pulse-gold">
              Ücretsiz Fal Baktır <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/giris" data-testid="hero-cta-login" className="btn-ghost px-8 py-3.5 rounded-full text-base">
              Giriş Yap
            </Link>
          </div>
          <div className="mt-10 flex items-center gap-6 text-sm text-white/50">
            <div><span className="gold-text font-semibold text-lg">4.8</span> · App Store</div>
            <div className="h-4 w-px bg-white/10" />
            <div><span className="gold-text font-semibold text-lg">200K+</span> yorumlanan fal</div>
          </div>
        </div>

        <div className="relative">
          <div
            className="aspect-[3/4] rounded-3xl overflow-hidden gold-border gold-glow relative"
            style={{ backgroundImage: `url(${HERO_IMAGES.main})`, backgroundSize: "cover", backgroundPosition: "center" }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-[#0B0E14]/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="glass rounded-2xl p-4">
                <p className="font-serif-myst text-lg italic gold-text">"Kısmet, sabreden kalbin ödülüdür."</p>
                <p className="text-xs text-white/50 mt-1">— Bugünün rehberi</p>
              </div>
            </div>
          </div>
          <div className="absolute -bottom-4 -left-4 glass rounded-2xl p-4 w-40 hidden sm:block">
            <div className="text-xs text-white/50">Bugünün burcu</div>
            <div className="font-serif-myst text-xl gold-text">Balık</div>
            <div className="text-xs text-white/60">Şanslı sayı: 7</div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <h2 className="font-serif-myst text-4xl md:text-5xl max-w-xl">
            Tek bir uygulama, <span className="gold-text italic">dokuz farklı fal</span>.
          </h2>
          <p className="text-white/60 text-sm max-w-sm">Her fal türü kendine özgü ustalıkla yorumlanır. AI, gerçek fal geleneklerinin bilgisiyle çalışır.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={f.name} data-testid={`feature-${f.name}`} className="rounded-2xl overflow-hidden border border-white/5 hover:border-[#D4AF37]/40 transition-colors group relative">
                <div className={`w-full ${i === 0 ? "aspect-[4/3]" : "aspect-square"} bg-cover bg-center relative`}
                     style={{ backgroundImage: `url(${FORTUNE_COVERS[f.key]})` }}>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-[#0B0E14]/40 to-transparent group-hover:from-[#0B0E14]/95 transition-colors" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <Icon className="w-6 h-6 gold-text mb-3 opacity-90" />
                    <div className="font-serif-myst text-2xl">{f.name}</div>
                    <div className="text-sm text-white/70 mt-1">{f.desc}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-16 text-center relative">
        <div className="absolute inset-0 overflow-hidden -z-10 opacity-30">
          <div
            className="w-full h-full bg-cover bg-center"
            style={{ backgroundImage: `url(${HERO_IMAGES.cosmic})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0E14] via-transparent to-[#0B0E14]" />
        </div>
        <h2 className="font-serif-myst text-4xl md:text-6xl mb-6">Hemen başla, <span className="gold-text italic">15 jeton</span> hediye.</h2>
        <p className="text-white/70 mb-8 max-w-lg mx-auto text-lg">Kayıt ol, uygulamayı keşfet. İlk falın bizden hediye.</p>
        <Link to="/kayit" data-testid="bottom-cta-register" className="btn-gold px-10 py-4 rounded-full text-lg inline-flex items-center gap-2 animate-pulse-gold">
          Ücretsiz Kayıt Ol <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-xs text-white/40">
        <p>Bu uygulama yalnızca eğlence amaçlıdır. © {new Date().getFullYear()} FalZilla · <Link to="/kvkk" className="hover:text-[#D4AF37]">KVKK</Link> · <Link to="/kullanim" className="hover:text-[#D4AF37]">Kullanım</Link></p>
      </footer>
    </div>
  );
}
